import { Typography } from '@mui/material'
import React, { Fragment, ChangeEvent, memo, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tw from 'twin.macro'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { SkipStep, UploadDOTNumber, UploadUSDOTNumber } from 'src/constants/actionTypes'
import { getCurrentCollectDataStep } from 'src/selectors/collectData'
import { Formik, FormikProps } from 'formik'
import { CollectDataStepType, MedicalCardRequirement } from 'src/constants/collect-data/enums'
import { getDOTNumberSchema, getUSDOTNumberSchema } from './DOTNumberForm.validator'
import {
  DOTNumberFormValues,
  DOTNumberRequest,
  USDOTNumberRequest,
} from './DOTNumberForm.interface'
import { DOTFormType } from './DOTNumberForm.constants'
import { useStepFormStorage } from 'src/hooks/useStepFormStorage'
import { ShipmentMajor } from '@shopify/polaris-icons'
import { Button } from '../shared/Button'
import { FormErrorMessage } from '../shared/form/FormErrorMessage'
import { FormikRadioGroup } from '../shared/form/formik/FormikRadioGroup'
import { FormikFileInput } from '../shared/form/formik/FormikFileInput'
import { FormikTextField } from '../shared/form/formik/FormikTextField'
import { USDOTNumberContent } from './USDOTNumberContent'
import { FormikSelect } from '../shared/form/formik/FormikSelect'
import { states } from 'src/constants/states'
import { DOTNumberDetails } from './DOTNumberDetails'
import { StepLabels } from '../client/step-labels/StepLabels'

const numberOptions = [
  { label: 'I want to submit DOT number', value: DOTFormType.DOT_NUMBER },
  { label: 'I want to submit USDOT number', value: DOTFormType.USDOT_NUMBER },
  { label: "I don't have a DOT/USDOT number", value: DOTFormType.NO_NUMBER },
]

const initialValues: DOTNumberFormValues = {
  file: undefined,
  dotNumber: '',
  stateOfIssue: '',
  usDotNumber: '',
  lastSubmittedAt: '',
  type: DOTFormType.DOT_NUMBER,
}

const getValidationSchema = (type: DOTFormType, medicalCardRequirement: MedicalCardRequirement) => {
  const schemaMap = {
    [DOTFormType.DOT_NUMBER]: getDOTNumberSchema(medicalCardRequirement),
    [DOTFormType.USDOT_NUMBER]: getUSDOTNumberSchema(medicalCardRequirement),
    [DOTFormType.NO_NUMBER]: undefined,
  }

  return schemaMap[type]
}

const DOTNumberFormComponent = () => {
  const dispatch = useDispatch()
  const currentStep = useSelector(getCurrentCollectDataStep)
  const formikRef = useRef<FormikProps<DOTNumberFormValues>>(null)

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [isUSDOTNumberReadyForUpload, setIsUSDOTNumberReadyForUpload] = useState<boolean>(false)

  const { savedValues, onValuesChange, clearStorage } = useStepFormStorage<DOTNumberFormValues>({
    storageKey: `step_dot_form_${currentStep?.id}`,
    fileFields: ['file'],
  })

  const [currentFormType, setCurrentFormType] = useState<DOTFormType>(
    (savedValues?.type as DOTFormType) ?? DOTFormType.DOT_NUMBER,
  )

  const medicalCardRequirement =
    (currentStep?.type === CollectDataStepType.DOTNumber
      ? currentStep.metadata?.medicalCardRequirement
      : undefined) ?? MedicalCardRequirement.Required

  const handleFormSubmit = async (values: DOTNumberFormValues) => {
    if (!currentStep?.id) {
      return
    }

    setIsLoading(true)
    setError(null)

    let formattedDate = ''

    if (values.lastSubmittedAt) {
      const [month, day, year] = values.lastSubmittedAt.split('/')
      formattedDate = `${year}-${month}-${day}`
    }

    try {
      const currentType = (values.type as DOTFormType) || DOTFormType.DOT_NUMBER

      if (currentType === DOTFormType.DOT_NUMBER) {
        const requestBody: DOTNumberRequest = {
          dotNumber: values.dotNumber || '',
          stateOfIssue: values.stateOfIssue || '',
          stepId: currentStep.id,
          lastSubmittedAt: formattedDate,
        }

        if (values.file && medicalCardRequirement !== MedicalCardRequirement.Hidden) {
          requestBody.file = values.file
        }

        await createAsyncAction(dispatch, UploadDOTNumber.request(requestBody))
      } else if (currentType === DOTFormType.USDOT_NUMBER) {
        const requestBody: USDOTNumberRequest = {
          usDotNumber: values.usDotNumber || '',
          stepId: currentStep.id,
          lastSubmittedAt: formattedDate,
        }

        if (values.file && medicalCardRequirement !== MedicalCardRequirement.Hidden) {
          requestBody.file = values.file
        }

        await createAsyncAction(dispatch, UploadUSDOTNumber.request(requestBody))
      } else if (currentType === DOTFormType.NO_NUMBER) {
        const requestBody = {
          stepId: currentStep.id,
        }

        await createAsyncAction(dispatch, SkipStep.request(requestBody))
      }

      clearStorage()
    } catch (err: any) {
      setError(err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const newType = event.target.value as DOTFormType

    setCurrentFormType(newType)
    setIsUSDOTNumberReadyForUpload(false)

    if (formikRef.current) {
      formikRef.current.setFieldValue('type', newType)
    }
  }

  const radioOptions = useMemo(() => {
    if (currentStep?.optional) {
      return numberOptions
    }

    return numberOptions.filter((option) => {
      return option.value !== DOTFormType.NO_NUMBER
    })
  }, [currentStep?.optional])

  const formInitialValues = savedValues ?? initialValues

  return (
    <Formik
      initialValues={formInitialValues}
      enableReinitialize
      onSubmit={handleFormSubmit}
      validationSchema={getValidationSchema(currentFormType, medicalCardRequirement)}
      innerRef={formikRef}
    >
      {({ handleSubmit, handleChange, values }) => {
        onValuesChange(values)

        const currentType = (values.type as DOTFormType) || DOTFormType.DOT_NUMBER

        return (
          <div css={tw`h-full w-full flex flex-col`}>
            <div>
              <div
                css={tw`w-11 h-11 flex justify-center items-center [border-radius: 22px] bg-shades-100 mt-2 mb-4`}
              >
                <ShipmentMajor width={20} />
              </div>
              <StepLabels />
              <DOTNumberDetails />

              <div css={tw`mt-6`}>
                <Typography
                  css={tw`text-shades-400 text-xs font-bold uppercase`}
                  variant="overline"
                >
                  Select preferable option
                </Typography>

                <div css={tw`bg-shades-0 p-4 rounded-xl mt-1.5 relative overflow-hidden`}>
                  <div css={tw`w-full mb-4`}>
                    <FormikRadioGroup
                      name="type"
                      options={radioOptions}
                      onChange={handleTypeChange}
                      dataTestId="contractors-onboarding-dot-form-type-input"
                    />
                  </div>

                  {currentType === DOTFormType.DOT_NUMBER && (
                    <Fragment>
                      <div css={tw`mb-4`}>
                        <FormikTextField
                          name="dotNumber"
                          placeholder="Enter DOT Number"
                          label="DOT Number"
                          mask={[/\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/, /\d/]}
                          onChange={handleChange}
                          css={tw`w-full`}
                          inputProps={{ 'data-testid': 'contractors-onboarding-dot-number-input' }}
                        />
                      </div>

                      <div css={tw`mb-4`}>
                        <FormikSelect
                          name="stateOfIssue"
                          label="State of issue"
                          placeholder="Select state"
                          options={states}
                          onChange={handleChange}
                          inputProps={{ 'data-testid': 'contractors-onboarding-dot-state-input' }}
                        />
                      </div>

                      <div css={tw`mb-4`}>
                        <FormikTextField
                          name="lastSubmittedAt"
                          placeholder="Enter date"
                          label="Date of last submitted MCS-150 (Motor Carrier Identification Report)"
                          mask={[/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]}
                          onChange={handleChange}
                          css={tw`w-full`}
                          inputProps={{ 'data-testid': 'contractors-onboarding-dot-date-input' }}
                        />
                      </div>
                    </Fragment>
                  )}

                  {currentType === DOTFormType.USDOT_NUMBER && (
                    <USDOTNumberContent
                      handleChange={handleChange}
                      setIsUSDOTNumberReadyForUpload={setIsUSDOTNumberReadyForUpload}
                    />
                  )}

                  {medicalCardRequirement !== MedicalCardRequirement.Hidden &&
                    (currentType === DOTFormType.DOT_NUMBER ||
                      (currentType === DOTFormType.USDOT_NUMBER &&
                        isUSDOTNumberReadyForUpload)) && (
                      <div css={tw`mt-6`}>
                        <Typography css={tw`mb-1.5 text-shades-400`}>
                          {medicalCardRequirement === MedicalCardRequirement.Optional
                            ? 'Upload DOT Medical Card (Optional)'
                            : 'Upload DOT Medical Card'}
                        </Typography>
                        <FormikFileInput
                          name="file"
                          inputDataTestId="contractors-onboarding-dot-medical-file-input"
                        />
                      </div>
                    )}
                </div>
                {(currentType === DOTFormType.DOT_NUMBER ||
                  (currentType === DOTFormType.USDOT_NUMBER && isUSDOTNumberReadyForUpload) ||
                  (currentType === DOTFormType.NO_NUMBER && !!currentStep?.optional)) && (
                  <Button
                    className={tw`w-full mt-6`}
                    loading={isLoading}
                    onClick={() => handleSubmit()}
                    data-testid="contractors-onboarding-dot-submit-btn"
                  >
                    Submit and Continue
                  </Button>
                )}
              </div>
            </div>
            <FormErrorMessage css={tw`mb-4`} error={error} />
          </div>
        )
      }}
    </Formik>
  )
}

export const DOTNumberForm = memo(DOTNumberFormComponent)
